import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../services/api';
import { PageHeader, PageDescription } from '../main/LayoutStyles';
import { Card, AlertBox } from '../main/CommonStyles';
import { Loader2, AlertTriangle } from 'lucide-react';
import styled from 'styled-components';

const SettingsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem; /* Zmniejszony odstęp */
`;

const SettingItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 1rem; /* Zmniejszony padding pionowy */
    background-color: #fff;
    border: 1px solid ${({ theme }) => theme.colors.borderColor};
    border-radius: 8px;

    span {
        font-weight: 500;
        font-size: 0.9rem;
    }
`;

const ToggleSwitch = styled.label`
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
    cursor: pointer;

    input {
        opacity: 0;
        width: 0;
        height: 0;
    }

    .slider {
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background-color: #ccc;
        transition: .4s;
        border-radius: 34px;
    }

    .slider:before {
        position: absolute;
        content: "";
        height: 16px;
        width: 16px;
        left: 4px;
        bottom: 4px;
        background-color: white;
        transition: .4s;
        border-radius: 50%;
    }

    input:checked + .slider {
        background-color: ${({ theme }) => theme.colors.primary};
    }

    input:checked + .slider:before {
        transform: translateX(20px);
    }
`;

const featureDisplayMap = {
    AI_CHAT: { name: 'Czat AI' }, JOURNAL: { name: 'Dziennik' },
    EXERCISES: { name: 'Ćwiczenia' }, TESTS: { name: 'Testy' },
    KNOWLEDGE_BASE: { name: 'Baza Wiedzy' }, ARTICLE_CREATION: { name: 'Tworzenie Artykułów' },
    APPOINTMENTS: { name: 'Wizyty' }, PSYCHOLOGIST_LIST: { name: 'Lista Specjalistów' },
    REPORTING: { name: 'Zgłaszanie Spraw' }, HELP: { name: 'Pomoc' },
    ADMIN_PANEL: { name: 'Panel Admina Organizacji' }, SPECIALIST_PANEL: { name: 'Panel Specjalisty' },
    SYSTEM_ADMIN_PANEL: { name: 'Panel System Admina' }
};

const SystemFeatureManagement = () => {
    const [systemToggles, setSystemToggles] = useState([]);
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState('');

    const fetchData = useCallback(async () => {
        setStatus('loading');
        try {
            const response = await apiClient.get('/features/system-admin/system-toggles');
            setSystemToggles(response.data);
            setStatus('success');
        } catch (err) {
            setError("Nie udało się załadować konfiguracji funkcji.");
            setStatus('error');
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSystemToggleChange = async (featureName, currentEnabled) => {
        setStatus('loading');
        try {
            await apiClient.put('/features/system-admin/system-toggles', { featureName, enabled: !currentEnabled });
            await fetchData();
        } catch (err) {
            setError("Błąd podczas aktualizacji funkcji systemowej.");
            setStatus('error');
        }
    };

    if (status === 'loading') {
        return <div style={{textAlign: 'center', padding: '2rem'}}><Loader2 className="icon-spin" size={32} /></div>;
    }

    return (
        <div>
            <PageHeader>Ustawienia Aplikacji (Funkcje Globalne)</PageHeader>
            <PageDescription>Włącz lub wyłącz poszczególne moduły w całej aplikacji. Wyłączenie funkcji tutaj sprawi, że będzie ona niedostępna dla wszystkich użytkowników.</PageDescription>
            {error && <AlertBox type="error" style={{marginBottom: '1rem'}}>{error}</AlertBox>}
            <Card>
                <SettingsList>
                    {Object.entries(featureDisplayMap).map(([featureName, { name }]) => {
                        const toggle = systemToggles.find(t => t.featureName === featureName);
                        const isEnabled = toggle ? toggle.enabled : false;
                        return (
                            <SettingItem key={featureName}>
                                <span>{name}</span>
                                <ToggleSwitch>
                                    <input
                                        type="checkbox"
                                        checked={isEnabled}
                                        onChange={() => handleSystemToggleChange(featureName, isEnabled)}
                                        disabled={status === 'loading'}
                                    />
                                    <span className="slider"></span>
                                </ToggleSwitch>
                            </SettingItem>
                        );
                    })}
                </SettingsList>
            </Card>
        </div>
    );
};

export default SystemFeatureManagement;